import { useMemo } from 'react'
import { Link } from 'react-router'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  buildPackagingItemIssuesDetailHref,
  type PackagingItemIssuesDashboardGroup,
  type PackagingItemIssuesDashboardSection,
  type PackagingItemIssuesDashboardSectionId,
} from '@/shared/hooks/api/packagingItemIssues'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'

const GROUP_ORDER: PackagingItemIssuesDashboardGroup[] = [
  'inconsistent',
  'missing',
  'non_compliant',
]

type PackagingItemIssuesSectionListProps = {
  sections: PackagingItemIssuesDashboardSection[]
}

function formatItemLabel(
  item: PackagingItemIssuesDashboardSection['items'][number]
): string {
  const skuPart =
    item.sku != null && item.sku.trim() !== '' ? item.sku.trim() : '—'
  return `${item.name} · ${skuPart} · ${item.supplierName}`
}

function PackagingItemIssuesSectionDescription({
  sectionId,
}: {
  sectionId: PackagingItemIssuesDashboardSectionId
}) {
  const { t } = useTranslation('packagingSuppliers')

  return (
    <p className='text-sm text-muted-foreground'>
      {t(`issuesDashboard.sections.${sectionId}.description`)}
    </p>
  )
}

function PackagingItemIssuesSectionRow({
  section,
  defaultOpen,
}: {
  section: PackagingItemIssuesDashboardSection
  defaultOpen: boolean
}) {
  const { t } = useTranslation('packagingSuppliers')
  const isComingSoon = section.status === 'coming_soon'

  if (isComingSoon) {
    return (
      <div className='rounded-lg border border-border opacity-50'>
        <div className='flex w-full flex-col gap-2 px-4 py-3 text-start'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='font-medium'>
              {t(`issuesDashboard.sections.${section.id}.title`)}
            </span>
            <Badge variant='secondary'>
              {t('issuesDashboard.comingSoon')}
            </Badge>
          </div>
          <PackagingItemIssuesSectionDescription sectionId={section.id} />
        </div>
      </div>
    )
  }

  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className='rounded-lg border border-border'
    >
      <CollapsibleTrigger
        className='flex w-full items-start justify-between gap-3 px-4 py-3 text-start hover:bg-muted/50'
      >
        <div className='flex min-w-0 flex-1 flex-col gap-2'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='font-medium'>
              {t(`issuesDashboard.sections.${section.id}.title`)}
            </span>
            <Badge variant={section.itemCount > 0 ? 'destructive' : 'secondary'}>
              {section.itemCount}
            </Badge>
          </div>
          <PackagingItemIssuesSectionDescription sectionId={section.id} />
        </div>
        <ChevronDown className='mt-1 size-4 shrink-0 transition-transform duration-200 in-data-panel-open:rotate-180' />
      </CollapsibleTrigger>
      <CollapsibleContent className='border-t border-border px-4 py-3'>
        {section.items.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            {t('issuesDashboard.noItems')}
          </p>
        ) : (
          <ul className='space-y-2'>
            {section.items.map((item) => (
              <li key={item.id}>
                <Link
                  className='text-sm text-primary underline-offset-4 hover:underline'
                  to={buildPackagingItemIssuesDetailHref({
                    supplierId: item.supplierId,
                    itemId: item.id,
                    sectionId: section.id,
                  })}
                >
                  {formatItemLabel(item)}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}

export function PackagingItemIssuesSectionList({
  sections,
}: PackagingItemIssuesSectionListProps) {
  const { t } = useTranslation('packagingSuppliers')

  const orderedSections = useMemo(() => {
    const ordered: PackagingItemIssuesDashboardSection[] = []
    for (const group of GROUP_ORDER) {
      const groupSections = sections.filter((section) => section.group === group)
      ordered.push(...groupSections)
    }
    return ordered
  }, [sections])

  const defaultOpenBySectionId = useMemo(() => {
    const openById = new Map<string, boolean>()
    let readySectionIndex = 0
    for (const section of orderedSections) {
      if (section.status === 'coming_soon') {
        continue
      }
      openById.set(section.id, readySectionIndex === 0)
      readySectionIndex += 1
    }
    return openById
  }, [orderedSections])

  const sectionsByGroup = useMemo(() => {
    const grouped = new Map<
      PackagingItemIssuesDashboardGroup,
      PackagingItemIssuesDashboardSection[]
    >()
    for (const group of GROUP_ORDER) {
      grouped.set(group, [])
    }
    for (const section of sections) {
      const groupSections = grouped.get(section.group)
      if (groupSections != null) {
        groupSections.push(section)
      }
    }
    return grouped
  }, [sections])

  return (
    <div className='space-y-8'>
      {GROUP_ORDER.map((group) => {
        const groupSections = sectionsByGroup.get(group) ?? []
        if (groupSections.length === 0) {
          return null
        }
        return (
          <section key={group} className='space-y-3'>
            <h2 className='text-lg font-semibold'>
              {t(`issuesDashboard.groups.${group}`)}
            </h2>
            <div className='space-y-3'>
              {groupSections.map((section) => (
                <PackagingItemIssuesSectionRow
                  key={section.id}
                  section={section}
                  defaultOpen={defaultOpenBySectionId.get(section.id) === true}
                />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

export type { PackagingItemIssuesDashboardSectionId }
